import React, { useState } from 'react';
import { Stack, Group, Button, Modal, Text, Paper, Box } from '@mantine/core';
import { IconEye, IconX } from '@tabler/icons-react';
import MDEditor from '@uiw/react-md-editor';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import classes from './MarkdownEditor.module.css';
import { defaultMarkdownComponents } from './MarkdownComponents';

// -----------------------------------------------------------------------------
interface MarkdownEditorProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  withAsterisk?: boolean;
  minRows?: number;
  error?: string;
}

// -----------------------------------------------------------------------------
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  withAsterisk = false,
  minRows = 4,
  error,
}) => {
  const [previewOpened, setPreviewOpened] = useState(false);

  // ---------------------------------------------------------------------------
  const handlePreviewOpen = () => {
    setPreviewOpened(true);
  };

  // ---------------------------------------------------------------------------
  const handlePreviewClose = () => {
    setPreviewOpened(false);
  };

  // ---------------------------------------------------------------------------
  return (
    <>
      <Stack gap="xs">
        {label && (
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              {label}
              {withAsterisk && (
                <Text component="span" c="red" ml={4}>
                  *
                </Text>
              )}
            </Text>
            <Button
              variant="light"
              size="xs"
              leftSection={<IconEye size={14} />}
              onClick={handlePreviewOpen}
              disabled={!value.trim()}
            >
              Preview
            </Button>
          </Group>
        )}{' '}
        <Box>
          {' '}
          <MDEditor
            value={value}
            onChange={(val) => onChange(val || '')}
            data-color-mode="light"
            visibleDragbar={false}
            hideToolbar={disabled}
            preview="edit"
            height={minRows * 25 + 100}
            className={
              disabled
                ? classes.mdEditorContainerDisabled
                : classes.mdEditorContainer
            }
          />
        </Box>
        {error && (
          <Text size="xs" c="red">
            {error}
          </Text>
        )}
      </Stack>

      {/* Preview Modal */}
      <Modal
        opened={previewOpened}
        onClose={handlePreviewClose}
        title={
          <Group gap="xs">
            <IconEye size={18} />
            <Text fw={600}>Markdown Preview</Text>
          </Group>
        }
        size="lg"
        scrollAreaComponent={Box}
      >
        <Paper p="md" withBorder className={classes.previewPaper}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={defaultMarkdownComponents}
          >
            {value}
          </ReactMarkdown>
        </Paper>
        <Group justify="flex-end" mt="md">
          <Button
            variant="light"
            leftSection={<IconX size={16} />}
            onClick={handlePreviewClose}
          >
            Close Preview
          </Button>
        </Group>
      </Modal>
    </>
  );
};
